import { useTranslation } from "react-i18next";
import { SafeText } from "@/shared/components/SafeText";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import type { UserDetail } from "../types";
import { AssignmentList } from "./AssignmentList";
import { membershipLocationRows, ownedLocationsFor } from "./membership-rows";
import { MembershipRole, type AdminMembership } from "@/shared/organizations/membership-types";

interface MembershipLocationsProps {
  readonly membership: AdminMembership;
  readonly user: UserDetail;
  readonly canManage: boolean;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (entityId: string) => void;
}

/**
 * STAFF: the assigned locations (active and inactive, selectable by admins).
 * OWNER: the organization's own locations, taken from the page's user detail
 * since the memberships read lists locations for STAFF only. ADMIN: a neutral
 * line (the read lists none, so emptiness is never asserted).
 */
export function MembershipLocations({
  membership,
  user,
  canManage,
  selected,
  onToggle,
}: MembershipLocationsProps) {
  const { t } = useTranslation();
  const name = sanitizeDisplayText(membership.organization.name);

  if (membership.role === MembershipRole.STAFF) {
    const rows = membershipLocationRows(membership);
    if (rows.length === 0) {
      return (
        <p data-testid="membership-no-locations" className="text-xs text-muted-foreground">
          {t("users.memberships.noAssigned")}
        </p>
      );
    }
    return (
      <AssignmentList
        label={t("users.memberships.assignedLocationsOf", { name })}
        rows={rows}
        canManage={canManage}
        selected={selected}
        onToggle={onToggle}
      />
    );
  }

  if (membership.role === MembershipRole.ADMIN) {
    // The read carries no locations for an ADMIN, and the user detail only
    // knows the user's own venues: say so instead of asserting "no locations".
    return (
      <p data-testid="membership-admin-locations" className="text-xs text-muted-foreground">
        {t("users.memberships.adminLocations")}
      </p>
    );
  }

  const owned = ownedLocationsFor(user, membership);
  if (owned.length === 0) {
    return (
      <p data-testid="membership-no-locations" className="text-xs text-muted-foreground">
        {t("users.memberships.noOwned")}
      </p>
    );
  }
  return (
    <ul
      aria-label={t("users.memberships.ownedLocationsOf", { name })}
      className="divide-y divide-border rounded-md border border-border"
    >
      {owned.map((l) => (
        <li
          key={`${l.kind}:${l.id}`}
          data-testid={`membership-location-${l.id}`}
          className="px-3 py-2 text-sm"
        >
          <p className="truncate font-medium">
            <SafeText>{l.name}</SafeText>
          </p>
          <p className="text-xs text-muted-foreground">{t(`users.memberships.kind.${l.kind}`)}</p>
        </li>
      ))}
    </ul>
  );
}
